import { useState, useEffect } from 'react';
import { useS3Upload } from './useS3FileUpload';

interface S3FieldOptions {
  initialValue?: string | string[] | null;
  onUpload: (filename: string | string[]) => void;
  notify?: (success: boolean, message: string) => void;
  multiple?: boolean;
  maxFileSize?: number;
  maxTotalSize?: number;
}

export function useS3Field({ 
  initialValue, 
  onUpload, 
  notify, 
  multiple = false,
  maxFileSize,
  maxTotalSize 
}: S3FieldOptions) {
  const { uploading, uploadSingle, uploadMultiple, getSignedUrl } = useS3Upload(notify);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewUrls, setPreviewUrls] = useState<Array<{ url: string; type: string; filename: string }>>([]);
  const [loaded, setLoaded] = useState(false);

  // Load previews once
  useEffect(() => {
    if (loaded || !initialValue) return;

    const load = async () => {
      if (multiple && Array.isArray(initialValue)) {
        const previews = await Promise.all(
          initialValue.map(async (filename) => {
            if (!filename || typeof filename !== 'string') return null;
            const url = await getSignedUrl(filename);
            if (!url) return null;
            
            const ext = filename.split('.').pop() || '';
            const typeMap: Record<string, string> = {
              pdf: 'application/pdf',
              png: 'image/png',
              jpg: 'image/jpeg',
              jpeg: 'image/jpeg',
              gif: 'image/gif',
            };
            
            return { url, filename, type: typeMap[ext] || 'application/octet-stream' };
          })
        );
        setPreviewUrls(previews.filter((p): p is { url: string; filename: string; type: string } => p !== null));
      } else if (typeof initialValue === 'string' && !initialValue.startsWith('http')) {
        const url = await getSignedUrl(initialValue);
        if (url) setPreviewUrl(url);
      }
      setLoaded(true);
    };

    load();
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    try {
      if (multiple) {
        const results = await uploadMultiple(Array.from(files), maxTotalSize);
        const filenames = results.map(r => r.filename);
        onUpload(filenames);
        setPreviewUrls(results.map(r => ({ url: r.previewUrl, filename: r.filename, type: r.type })));
      } else {
        const result = await uploadSingle(files[0], maxFileSize);
        onUpload(result.filename);
        setPreviewUrl(result.previewUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const refreshPreview = async (filename: string) => {
    const url = await getSignedUrl(filename);
    if (url) setPreviewUrl(url);
  };

  return {
    uploading,
    previewUrl,
    previewUrls,
    handleUpload,
    refreshPreview,
    getSignedUrl,
  };
}